import { useRef, useState } from "react";
import Modal from "react-responsive-modal";
import { useSSRHook } from "../../ssrh/client";
import { Loading, Remove } from ".";
import { useUser } from "../User";

import { ID, Sponsor } from "../../types";
import Icon from "@mdi/react";
import { mdiDrag, mdiPlus } from "@mdi/js";
import { api } from "../services/api";
import { Draggable, moveItems, useDraggable, useDraggableContext } from "react-tiny-dnd";
import { DraggableContextType } from "react-tiny-dnd/lib/types";
import { resizeImage } from "easy-image-resizer";

export default function Sponsors() {
    const user = useUser();

    const [sponsors, error, isLoading, reload] = useSSRHook<Sponsor[]>("/api/sponsor");

    const [showEdit, setShowEdit] = useState(false);
    const [message, setMessage] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    // drag list
    const onDrop = async (dragIndex: number, overIndex: number) => {
        if (!sponsors) return;

        const nextSponsors = moveItems(sponsors, dragIndex, overIndex).map((sponsor, order) => ({ ...sponsor, order }));

        for (const sponsor of nextSponsors) {
            await api.updateSponsor(sponsor);
        }
        setMessage("Pořadí sponzorů upraveno");
        reload();
    };

    const context = useDraggableContext({ onDrop });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function handleAddSponsors(e: any) {
        if (!sponsors) return;

        const files = e.target.files as File[];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) continue;

            setMessage("Přidávám obrázek...");
            const url = await resizeImage(file, {
                maxHeight: 70 * 2,
                type: "webp",
            });
            await api.addSponsor({
                _id: "",
                order: sponsors.length,
                logo: { type: "EASY_DB_FILE", url },
                name: file.name,
                url: "https://tsokrkonose.cz/",
            });
        }

        reload();
        setMessage("Obrázeky sponzorů byly přidány");
    }

    async function handleChange(sponsor: Sponsor, { name, url }: { name: string, url: string }) {
        await api.updateSponsor({ ...sponsor, name, url });
        reload();
        setMessage("Detail sponzora byly změněny");
    }

    async function handleRemove(sponsorId: ID) {
        setMessage("Sponzor se maže...");
        await api.removeSponsor({ sponsorId });
        reload();
        setMessage("Sponzor byl smazán");
    }

    return <div className="sponsors">
        {user.isAdmin && <div className="admin">
            <button onClick={() => setShowEdit(true)}>Upravit sponzory</button>
            <Modal
                open={showEdit}
                onClose={() => setShowEdit(false)}
                styles={{ modal: { width: "550px" } }}
            >
                <h3>Upravit sponzory</h3>
                {message && <p className="alert">{message}</p>}
                <div className="list no-select">
                    {sponsors?.map((sponsor, i) => (
                        <DraggableItem
                            key={sponsor._id}
                            context={context}
                            sponsor={sponsor}
                            index={i}
                            onChange={(change) => handleChange(sponsor, change)}
                            onRemove={() => handleRemove(sponsor._id)}
                        />
                    ))}
                </div>
                <button
                    style={{ width: "100%" }}
                    onClick={() => inputRef.current?.click()}
                >
                    <Icon path={mdiPlus} size={1} />{" "}
                    Přidat sponzory
                </button>
                <input
                    style={{ display: "none" }}
                    type="file"
                    ref={inputRef}
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleAddSponsors}
                />
            </Modal>
        </div>}

        {isLoading && <Loading />}
        {sponsors?.map(sponsor => <a key={sponsor._id} target="__blank" href={sponsor.url}>
            <img src={sponsor.logo.url} alt={sponsor.name} />
        </a>)}
    </div>;
}

function DraggableItem(
    { context, index, sponsor, onChange, onRemove }: {
        context: DraggableContextType;
        index: number;
        sponsor: Sponsor;
        onChange: (change: { name: string, url: string }) => void;
        onRemove: () => void;
    },
) {
    const { listeners, isDragging } = useDraggable(context, index);

    const [name, setName] = useState(sponsor.name);
    const [url, setUrl] = useState(sponsor.url);

    return (
        <Draggable
            context={context}
            index={index}
            dividerClassName="dnd-divider"
        >
            <div className="item" style={{ opacity: isDragging ? 0.4 : 1 }}>
                <div className="dnd-button" {...listeners}>
                    <Icon path={mdiDrag} size={2} />
                </div>
                <div className="content">
                    <img
                        style={{ height: "70px" }}
                        className="carousel-edit-image"
                        src={sponsor.logo.url}
                    />
                    <input type="text" placeholder="Název sponzora" value={name}
                        onChange={e => setName(e.target.value)} />
                    <input type="url" placeholder="URL" value={url} onChange={e => setUrl(e.target.value)} />
                    {(name !== sponsor.name || url !== sponsor.url) && <button onClick={() => onChange({ name, url })}>
                        Uložit změny
                    </button>}
                </div>
                <Remove onRemove={onRemove} />
            </div>
        </Draggable>
    );
}
